import { NAV_ITEMS, isHiddenForPersona, navViewerPersona } from '@/constants/nav';
import { useWorkspace } from '@/contexts/WorkspaceProvider';
import { IMenuItem } from '@/types/nav';
import { FC, useMemo, useCallback, useId, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useProtectedLayoutContext } from '../context/ProtectedLayoutProvider';
import MenuItem from './MenuItem';
import { isSdrExperienceRolledOut } from '@/constants/sdrExperience';
import { isAeExperienceRolledOut } from '@/constants/aeExperience';
import { viewAsQueryString } from '@/constants/repExperience';
import { buildTasksHref } from '@/views/dashboard/buildTasksHref';
import { PATH } from '@/constants/path';
import { ChevronDown, ChevronRight, Sun } from 'lucide-react';

/**
 * Today carries its own icon.
 *
 * Today and Dashboard are the same nav entry split in two for a GTM workspace admin, so the
 * second one inherited the first's `LayoutDashboard` and the sidebar showed two identical icons
 * stacked — reported by Sunder, 2026-09-23. They are different destinations (`/dashboard` is the
 * rep's day, `/analytics/overview` is the workspace's numbers), so they need to look different.
 * A sun reads as "today" at 16px without colliding with Meetings' clock-on-a-calendar.
 */
const TODAY_ICON = Sun;
import { useUserContext } from '@/contexts/UserProvider';
import { useViewAsTarget } from '@/hooks/useViewAsTarget';

interface IMenuProps {
  onCloseSidebar?: () => void;
  isVisuallyExpanded?: boolean;
}

const Menu: FC<IMenuProps> = (props) => {
  const { isVisuallyExpanded: contextExpanded } = useProtectedLayoutContext();
  const workspaceContext = useWorkspace();
  const sectionId = useId();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const location = `${pathname}?${searchParams?.toString() ?? ''}`;
  const [sectionState, setSectionState] = useState<{ location: string; closed: string[] }>({ location, closed: [] });
  const closedSections = sectionState.location === location ? sectionState.closed : [];
  const toggleSection = (label: string) => setSectionState({ location, closed: closedSections.includes(label)
    ? closedSections.filter((value) => value !== label) : [...closedSections, label] });
  const { askIOTUserDetails } = useUserContext();
  // The sidebar labels follow whichever experience is actually on screen, so it
  // reads the same resolved view-as target Today and Actions do. A `?viewAs=`
  // parameter on its own says nothing about the target's experience — treating
  // it as an AE override is what made the nav disagree with the page
  // (mb-frontend#4573).
  const viewAsTarget = useViewAsTarget();
  const viewAsExperience = viewAsTarget.status === 'resolved' ? viewAsTarget.experience : null;
  const viewAsUserId = viewAsTarget.status === 'resolved' ? viewAsTarget.requestedUserId : null;
  const workspaceRole = workspaceContext?.workspaceRole ?? 'member';
  const isOwnAdmin = workspaceContext?.isAdmin === true;
  const hasAdminToday = isOwnAdmin && workspaceContext?.activeWorkspace?.gtm_signal_enabled === true;
  // A workspace admin drilling into an SDR keeps the admin nav: Admin Today is
  // still the page underneath, with that rep's Today embedded in it.
  const showAdminNav = isOwnAdmin && viewAsExperience !== 'ae';
  const showSdrNav = !showAdminNav && (viewAsExperience === 'sdr' || (viewAsExperience === null
    && isSdrExperienceRolledOut(
      workspaceContext?.isSdr,
      workspaceContext?.activeWorkspace?.vendorid,
      askIOTUserDetails?.user_id,
    )));
  const showAeNav = !showAdminNav && !showSdrNav && (viewAsExperience === 'ae' || (viewAsExperience === null
    && isAeExperienceRolledOut(
      workspaceContext?.isAe,
      workspaceContext?.activeWorkspace?.vendorid,
      askIOTUserDetails?.user_id,
    )));
  // Only AE Today carries a view-as target across navigation, because only it
  // has a view-as mode. /tasks is one hub for every persona (the AE Actions hub
  // was retired, mb-server#6639) and scopes by its own assignee filter, so a
  // drill-in of either experience keeps the rep in view through the filter
  // TasksHub already restores from the URL (`?assignee=`, see buildTasksHref) —
  // same context, no second impersonation mode.
  const viewAsQuery = showAeNav && viewAsUserId ? viewAsQueryString(viewAsUserId, 'ae') : '';
  const drillInUserId = viewAsExperience !== null ? viewAsTarget.requestedUserId : null;

  // Use prop if provided (for direct control), otherwise use context
  const isVisuallyExpanded = props.isVisuallyExpanded !== undefined ? props.isVisuallyExpanded : contextExpanded;

  // Items must never appear as "locked" when they fail product-module gating — hide entirely instead.
  const passesProductModuleGate = useCallback((item: IMenuItem) => {
    if (!item.requiredModule || !workspaceContext?.activeWorkspace) {
      return true;
    }
    return workspaceContext.hasModule(item.requiredModule);
  }, [workspaceContext]);

  // Returns true if the item should be visible at all (plan-level checks)
  const filterMenuItem = useCallback((item: IMenuItem) => {
    // If no workspace context (during loading), hide tier-restricted items to prevent flashing
    if (!workspaceContext) {
      return !item.requiresWorkspace && !item.allowedPlans;
    }

    // If item doesn't require workspace, always show it
    if (!item.requiresWorkspace) {
      return true;
    }

    // If item requires workspace, check if user has workspaces
    if (!workspaceContext.hasWorkspaces) {
      return false;
    }

    // GH#1280: Product module gating — hide items when vendor lacks the required module.
    // hasModule treats [] and undefined as "both enabled" so admin-cleared vendors don't
    // accidentally lose their whole nav (schema-level NOT NULL DEFAULT '{marketing,sales}'
    // makes [] unreachable in practice, but we stay consistent with FeatureGate +
    // settingsTabAccess + the other consumers of this normalizer).
    if (item.requiredModule && workspaceContext.activeWorkspace) {
      if (!workspaceContext.hasModule(item.requiredModule)) {
        return false;
      }
    }

    // #2694 Role-based visibility: hide items the viewer role isn't allowed to see.
    if (item.allowedRoles && item.allowedRoles.length > 0) {
      if (!item.allowedRoles.includes(workspaceRole)) {
        return false;
      }
    }

    // If still loading workspace context (activeWorkspace is null but hasWorkspaces is true),
    // show items without tier restrictions to prevent menu flashing
    if (workspaceContext.hasWorkspaces && !workspaceContext.activeWorkspace && workspaceContext.isLoading) {
      return !item.allowedPlans; // Feature-flagged items shown in locked state once workspace loads
    }

    // Feature-flagged items: always visible (shown locked when disabled) — never hidden
    // Locking is handled separately by isItemLocked()

    // NEW: Check tier-based access using allowedPlans
    if (item.allowedPlans && item.allowedPlans.length > 0) {
      const hasAccess = workspaceContext.hasAccessToTier(item.allowedPlans);
      return hasAccess;
    }

    // LEGACY: Fallback to requiresEnterprise for backward compatibility
    if (item.requiresEnterprise && !workspaceContext.isEnterprise) {
      return false;
    }

    // Show the item
    return true;
  }, [workspaceContext, workspaceRole]);

  // Returns true if the item should render in a locked/disabled state
  const isItemLocked = useCallback((item: IMenuItem): boolean => {
    if (!workspaceContext?.activeWorkspace) return false;

    if (item.requiresFeature === 'gtm_signal_enabled') {
      return !workspaceContext.activeWorkspace.gtm_signal_enabled;
    }
    if (item.requiresFeature === 'ai_seo_enabled') {
      return !workspaceContext.activeWorkspace.ai_seo_enabled;
    }
    if (item.requiresFeature === 'outbound_enabled') {
      return workspaceContext.activeWorkspace.outbound_enabled === false;
    }
    return false;
  }, [workspaceContext]);

  const viewerPersona = navViewerPersona(workspaceContext);

  // Returns true if the item should be hidden entirely (not just locked)
  const isItemHidden = useCallback((item: IMenuItem): boolean => {
    if (isHiddenForPersona(item, viewerPersona)) return true;
    if (!workspaceContext?.activeWorkspace) return false;

    // hideWhenFeatureDisabled: explicit hide flag (used on section headers and specific items)
    if (item.hideWhenFeatureDisabled === 'outbound_enabled') {
      return workspaceContext.activeWorkspace.outbound_enabled === false;
    }

    // Opt-in flag (default off): hide the Meetings item unless the workspace has it enabled.
    if (item.hideWhenFeatureDisabled === 'meetings_enabled') {
      return !workspaceContext.activeWorkspace.meetings_enabled;
    }

    if (item.requiresFeature === 'power_dialer_enabled') {
      return !workspaceContext.activeWorkspace.power_dialer_enabled;
    }
    return false;
  }, [workspaceContext, viewerPersona]);

  // GH#4247: Opportunities/Pipeline is product navigation for opportunity owners.
  // Visible only to AE-experience members, or to workspace admins using the
  // admin/standard experience (workspace-scoped admin state, not a global role).
  // This is navigation, not authorization — route/permission guards stay intact.
  const isOpportunitiesNavVisible = showAeNav
    || (workspaceContext?.isAdmin === true && !showSdrNav && !showAeNav);

  const filteredNavItems = useMemo(() => {
    const visibleItems = NAV_ITEMS.map((item) => {
      if (!isOpportunitiesNavVisible && item.sectionItems) {
        return {
          ...item,
          sectionItems: item.sectionItems.filter(
            (sectionItem) => sectionItem.href !== PATH.OPPORTUNITIES,
          ),
        };
      }
      return item;
    }).filter((item) => {
      // Section headers: hide entirely when hideWhenFeatureDisabled triggers
      if (item.isSectionHeader) {
        if (isItemHidden(item)) return false;
        if (item.sectionItems) {
          return item.sectionItems.some(sectionItem => !isItemHidden(sectionItem)
            && (filterMenuItem(sectionItem) || (isItemLocked(sectionItem) && passesProductModuleGate(sectionItem))));
        }
        return false;
      }

      // Regular items: apply standard filtering
      return !isItemHidden(item) && filterMenuItem(item);
    });

    // The SDR and AE experiences keep the established navigation, permissions,
    // and product-module gates intact. Only user-facing labels change so the
    // entry points read as the rep's daily workspace.
    if (!showAdminNav && !showSdrNav && !showAeNav) return visibleItems;
    return visibleItems.map((item) => {
      if (!item.sectionItems) return item;
      return {
        ...item,
        sectionItems: item.sectionItems.flatMap((sectionItem) => {
          if (sectionItem.href === PATH.ANALYTICS_OVERVIEW) {
            // Admin analytics remains reachable even during a rep drill-in.
            if (isOwnAdmin) {
              const dashboard = { ...sectionItem, href: PATH.ADMIN_ANALYTICS };
              return hasAdminToday
                ? [
                  {
                    ...sectionItem,
                    label: 'Today',
                    icon: TODAY_ICON,
                    href: viewAsQuery ? `${sectionItem.href}?${viewAsQuery}` : sectionItem.href,
                  },
                  dashboard,
                ]
                : [sectionItem];
            }
            return {
              ...sectionItem,
              label: 'Today',
              icon: TODAY_ICON,
              href: viewAsQuery ? `${sectionItem.href}?${viewAsQuery}` : sectionItem.href,
            };
          }
          if (sectionItem.href === PATH.TASKS) {
            // One name for everybody; the SDR experience used to read "Actions (Tasks)".
            return {
              ...sectionItem,
              label: 'Actions',
              href: drillInUserId ? buildTasksHref({ assignee: drillInUserId }) : sectionItem.href,
            };
          }
          return sectionItem;
        }),
      };
    });
  }, [isOwnAdmin, hasAdminToday, showAdminNav, showSdrNav, showAeNav, viewAsQuery, drillInUserId, isOpportunitiesNavVisible, filterMenuItem, isItemHidden, isItemLocked, passesProductModuleGate]);

  return (
    <nav aria-label='Main navigation' className='mbn-nav'>
      <ul className='mbn-nav-list'>
        {filteredNavItems.map((item, index) => {
          const groupSpacing = index > 0 ? 'mbn-group' : '';
          // If section header, render header + filtered items
          if (item.isSectionHeader && item.sectionItems) {
            const filteredSectionItems = item.sectionItems.filter(sectionItem =>
              !isItemHidden(sectionItem)
              && (filterMenuItem(sectionItem) || (isItemLocked(sectionItem) && passesProductModuleGate(sectionItem)))
            );

            // Don't render empty sections
            if (filteredSectionItems.length === 0) {
              return null;
            }

            return (
              <li key={index} className={groupSpacing}>
                {isVisuallyExpanded && (
                  <button type='button' className='mbn-group-label'
                    aria-expanded={!closedSections.includes(item.label)} aria-controls={`${sectionId}-${index}`}
                    onClick={() => toggleSection(item.label)}>
                    <span>{item.label.charAt(0) + item.label.slice(1).toLowerCase()}</span>
                    {closedSections.includes(item.label) ? <ChevronRight aria-hidden='true' /> : <ChevronDown aria-hidden='true' />}
                  </button>
                )}
                <ul id={`${sectionId}-${index}`} className='mbn-section-items' hidden={isVisuallyExpanded && closedSections.includes(item.label)}>
                  {filteredSectionItems.map((sectionItem, sectionIndex) => (
                    <li key={sectionIndex}>
                      <MenuItem
                        {...props}
                        menuItem={sectionItem}
                        expandedSidebar={isVisuallyExpanded}
                        locked={isItemLocked(sectionItem)}
                      />
                    </li>
                  ))}
                </ul>
              </li>
            );
          }

          // Regular item (non-section)
          return (
            <li key={index} className={groupSpacing}>
              <MenuItem {...props} menuItem={item} expandedSidebar={isVisuallyExpanded} />
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default Menu;
